import { FormSchema } from "@/components/DynamicApplicationForm";

export const ATHLETE_SCHEMA_DEFAULT: FormSchema = {
  sections: [
    {
      id: "s1_personal",
      title: "SECTION 1: PERSONAL INFORMATION",
      description: "Tell us who you are.",
      fields: [
        { id: "full_name", label: "Full Name", type: "text", required: true },
        { id: "email", label: "Email Address", type: "email", required: true },
        { id: "phone", label: "Phone Number (WhatsApp Preferred)", type: "tel", required: true },
        { id: "date_of_birth", label: "Date of Birth", type: "date", required: true },
        { id: "gender", label: "Gender", type: "select", required: true, options: ["Male","Female","Non-binary","Prefer not to say"] },
        { id: "nationality", label: "Nationality", type: "text", required: true },
        { id: "country_of_residence", label: "Country of Residence", type: "text", required: true },
        { id: "current_address", label: "Current Address", type: "textarea", required: true },
        { id: "passport_number", label: "Passport Number", type: "text", required: false, description: "Optional" },
        { id: "profile_photo", label: "Profile Photo", type: "file", required: true, accept: "image/*", maxSizeMB: 5 },
      ]
    },
    {
      id: "s2_athlete_profile",
      title: "SECTION 2: ATHLETE PROFILE",
      description: "Tell us about your sporting background.",
      fields: [
        { id: "sport_discipline", label: "Sport / Discipline", type: "text", required: true },
        { id: "position_role", label: "Position / Playing Role", type: "text", required: true },
        { id: "height", label: "Height", type: "text", required: true, placeholder: "e.g. 180cm" },
        { id: "weight", label: "Weight", type: "text", required: true, placeholder: "e.g. 75kg" },
        { id: "dominant_foot_hand", label: "Dominant Foot / Hand", type: "radio", required: true, options: ["Left","Right","Both"] },
        { id: "current_club", label: "Current Club / Academy", type: "text", required: true },
        { id: "years_experience", label: "Years of Experience", type: "text", required: true },
        { id: "competition_level", label: "Current Competition Level", type: "checkboxes", required: true, options: ["Academy","Amateur","Semi-Professional","Professional","National Team"] },
        { id: "playing_style", label: "Preferred Playing Style", type: "textarea", required: false },
        { id: "languages_spoken", label: "Languages Spoken", type: "text", required: false, placeholder: "e.g. English, French" },
      ]
    },
    {
      id: "s3_contract",
      title: "SECTION 3: CLUB & CONTRACT STATUS",
      description: "Current Representation Status",
      fields: [
        { id: "registered_with_club", label: "Are you currently registered with a club?", type: "radio", required: true, options: ["Yes","No"] },
        { id: "club_name", label: "If Yes, Club Name", type: "text", required: false },
        { id: "active_contract", label: "Do you currently have an active playing contract?", type: "radio", required: true, options: ["Yes","No"] },
        { id: "contract_start", label: "Contract Start Date", type: "date", required: false },
        { id: "contract_end", label: "Contract End Date", type: "date", required: false },
        { id: "represented_by_agency", label: "Are you currently represented by another sports agency or agent?", type: "radio", required: true, options: ["Yes","No"] },
        { id: "agency_name", label: "Agency / Agent Name", type: "text", required: false },
        { id: "reason_seeking_rep", label: "Reason for seeking Eliada Sports Agency representation", type: "textarea", required: false },
      ]
    },
    {
      id: "s4_career",
      title: "SECTION 4: CAREER HISTORY",
      description: "Tell us about your sporting journey.",
      fields: [
        { id: "previous_clubs", label: "Previous Clubs / Academies", type: "textarea", required: true, description: "Provide club names and years.\n\nExample:\nLagos Stars FC (2021–2024)\nFuture Academy (2018–2021)" },
        { id: "national_team_exp", label: "National Team Experience", type: "radio", required: false, options: ["Yes","No"] },
        { id: "national_team_details", label: "If Yes, provide details", type: "textarea", required: false },
        { id: "major_competitions", label: "Major Competitions Participated In", type: "textarea", required: false },
        { id: "career_highlights", label: "Career Highlights & Achievements", type: "textarea", required: true, description: "Examples:\n• Championships won\n• Tournament appearances\n• Goals scored / Assists\n• Personal records\n• Awards and recognitions" },
      ]
    },
    {
      id: "s5_media",
      title: "SECTION 5: PERFORMANCE & MEDIA",
      description: "Help us see you in action.",
      fields: [
        { id: "highlight_video", label: "Highlight Video URL", type: "url", required: true, description: "Accepted: YouTube, Vimeo, Google Drive" },
        { id: "additional_videos", label: "Additional Video Links", type: "textarea", required: false },
        { id: "social_instagram", label: "Instagram", type: "url", required: false },
        { id: "social_facebook", label: "Facebook", type: "url", required: false },
        { id: "social_tiktok", label: "TikTok", type: "url", required: false },
        { id: "social_linkedin", label: "LinkedIn", type: "url", required: false },
        { id: "social_twitter", label: "X (Twitter)", type: "url", required: false },
        { id: "athletic_cv", label: "Upload Athletic CV / Sports Resume", type: "file", required: false, accept: ".pdf,.docx", maxSizeMB: 10, description: "Optional — PDF or DOCX" },
      ]
    },
    {
      id: "s6_goals",
      title: "SECTION 6: GOALS & ASPIRATIONS",
      description: "",
      fields: [
        { id: "why_join", label: "Why do you want to join Eliada Sports Agency?", type: "textarea", required: true },
        { id: "short_term_goals", label: "What are your short-term career goals?", type: "textarea", required: true },
        { id: "long_term_goals", label: "What are your long-term career goals?", type: "textarea", required: true },
        { id: "preferred_countries", label: "Which countries or leagues would you like to play in?", type: "text", required: false },
        { id: "support_needed", label: "What support are you looking for?", type: "checkboxes", required: false, options: ["Representation","Club Placement","Trials","Career Development","Sponsorship Opportunities","Branding & Media Visibility","Contract Negotiation","International Opportunities"] },
      ]
    },
    {
      id: "s7_source",
      title: "SECTION 7: APPLICATION SOURCE",
      description: "",
      fields: [
        { id: "how_heard", label: "How did you hear about Eliada Sports Agency?", type: "checkboxes", required: true, options: ["Social Media","Google Search","Friend / Referral","Coach","Club","Athlete","Event / Tournament","Website","Other"] },
        { id: "referral_name", label: "If Referral, who referred you?", type: "text", required: false },
      ]
    },
    {
      id: "s8_declaration",
      title: "SECTION 8: DECLARATION & CONSENT",
      description: "I certify that all information provided in this application is true, accurate, and complete. I authorize Eliada Sports Agency to verify any information provided. I consent to the collection and processing of my personal data in accordance with Eliada Sports Agency's Privacy Policy.",
      fields: [
        { id: "agree_terms", label: "I Agree", type: "checkbox", required: true },
        { id: "signature", label: "Electronic Signature", type: "signature", required: true, description: "Type your full name as your electronic signature" },
        { id: "declaration_date", label: "Date", type: "date", required: true },
      ]
    },
  ],
  submitButtonText: "SUBMIT ATHLETE APPLICATION",
  successMessage: "Thank you for submitting your athlete representation application. Our scouting and athlete management team will review your profile and contact you within 5–7 business days.",
};

export const COACH_SCHEMA_DEFAULT: FormSchema = {
  sections: [
    {
      id: "s1_personal",
      title: "SECTION 1: PERSONAL INFORMATION",
      description: "Tell us who you are.",
      fields: [
        { id: "full_name", label: "Full Name", type: "text", required: true },
        { id: "email", label: "Email Address", type: "email", required: true },
        { id: "phone", label: "Phone Number (WhatsApp Preferred)", type: "tel", required: true },
        { id: "date_of_birth", label: "Date of Birth", type: "date", required: true },
        { id: "gender", label: "Gender", type: "select", required: true, options: ["Male","Female","Non-binary","Prefer not to say"] },
        { id: "nationality", label: "Nationality", type: "text", required: true },
        { id: "country_of_residence", label: "Country of Residence", type: "text", required: true },
        { id: "current_address", label: "Current Residential Address", type: "textarea", required: true },
        { id: "profile_photo", label: "Profile Photograph", type: "file", required: true, accept: "image/*", maxSizeMB: 5 },
      ]
    },
    {
      id: "s2_professional",
      title: "SECTION 2: PROFESSIONAL PROFILE",
      description: "Your coaching background.",
      fields: [
        { id: "primary_sport", label: "Primary Sport / Discipline", type: "text", required: true },
        { id: "current_role", label: "Current Coaching Role", type: "text", required: true, description: "e.g. Head Coach, Assistant Coach, Academy Coach, Strength & Conditioning Coach, Technical Director" },
        { id: "current_club", label: "Current Club / Organization", type: "text", required: false },
        { id: "years_experience", label: "Years of Coaching Experience", type: "select", required: true, options: ["Less than 1 year","1–3 years","3–5 years","5–10 years","10–15 years","15+ years"] },
        { id: "highest_qualification", label: "Highest Educational Qualification", type: "text", required: false },
        { id: "languages_spoken", label: "Languages Spoken", type: "text", required: true },
      ]
    },
    {
      id: "s3_employment",
      title: "SECTION 3: EMPLOYMENT STATUS",
      description: "",
      fields: [
        { id: "employment_status", label: "Current Employment Status", type: "radio", required: true, options: ["Full-Time Coach","Part-Time Coach","Freelance Coach","Self-Employed","Unattached"] },
        { id: "working_with_org", label: "Are you currently working with a club, academy, sports organization, or agency?", type: "radio", required: true, options: ["Yes","No"] },
        { id: "org_name", label: "Organization Name", type: "text", required: false },
        { id: "position_held", label: "Position Held", type: "text", required: false },
        { id: "employment_start", label: "Start Date", type: "date", required: false },
        { id: "employment_end", label: "End Date (If Applicable)", type: "date", required: false },
      ]
    },
    {
      id: "s4_experience",
      title: "SECTION 4: COACHING EXPERIENCE",
      description: "Tell us about your coaching journey.",
      fields: [
        { id: "previous_clubs", label: "Previous Clubs / Academies", type: "textarea", required: true, description: "Include: Organization Name, Position Held, Years Served" },
        { id: "age_groups_coached", label: "Age Groups Coached", type: "checkboxes", required: true, options: ["Under 10","Under 13","Under 15","Under 17","Under 20","Senior Team","Professional Athletes"] },
        { id: "coaching_expertise", label: "Areas of Coaching Expertise", type: "checkboxes", required: true, options: ["Technical Development","Tactical Development","Physical Conditioning","Goalkeeping","Youth Development","Talent Identification","Athlete Mentorship","Performance Analysis"] },
      ]
    },
    {
      id: "s5_achievements",
      title: "SECTION 5: ACHIEVEMENTS & PLAYER DEVELOPMENT",
      description: "",
      fields: [
        { id: "coaching_achievements", label: "Coaching Achievements", type: "textarea", required: true, description: "e.g. Championships won, Team promotions, Awards received, Performance improvements" },
        { id: "notable_athletes", label: "Notable Athletes Mentored or Developed", type: "textarea", required: false },
        { id: "development_philosophy", label: "Player Development Philosophy", type: "textarea", required: true, description: "Describe your coaching philosophy and approach to athlete growth." },
      ]
    },
    {
      id: "s6_media",
      title: "SECTION 6: MEDIA & REFERENCES",
      description: "",
      fields: [
        { id: "cv_upload", label: "Professional CV / Resume", type: "file", required: true, accept: ".pdf,.docx", maxSizeMB: 10 },
        { id: "linkedin", label: "LinkedIn Profile", type: "url", required: false },
        { id: "website", label: "Website or Portfolio", type: "url", required: false },
        { id: "social_instagram", label: "Instagram", type: "url", required: false },
        { id: "social_facebook", label: "Facebook", type: "url", required: false },
        { id: "social_twitter", label: "X (Twitter)", type: "url", required: false },
        { id: "social_youtube", label: "YouTube", type: "url", required: false },
        { id: "training_videos", label: "Training Session Videos or Coaching Content Links", type: "textarea", required: false, description: "YouTube, Vimeo, or Google Drive links" },
        { id: "ref1_name", label: "Reference 1 — Full Name", type: "text", required: true },
        { id: "ref1_position", label: "Reference 1 — Position", type: "text", required: true },
        { id: "ref1_org", label: "Reference 1 — Organization", type: "text", required: true },
        { id: "ref1_contact", label: "Reference 1 — Email or Phone", type: "text", required: true },
        { id: "ref2_name", label: "Reference 2 — Full Name", type: "text", required: true },
        { id: "ref2_position", label: "Reference 2 — Position", type: "text", required: true },
        { id: "ref2_org", label: "Reference 2 — Organization", type: "text", required: true },
        { id: "ref2_contact", label: "Reference 2 — Email or Phone", type: "text", required: true },
      ]
    },
    {
      id: "s7_source",
      title: "SECTION 7: APPLICATION SOURCE",
      description: "",
      fields: [
        { id: "how_heard", label: "How did you hear about Eliada Sports Agency?", type: "checkboxes", required: true, options: ["Social Media","Google Search","Friend / Referral","Coach / Colleague","Club","Athlete","Website","Other"] },
        { id: "referral_name", label: "If Referral, who referred you?", type: "text", required: false },
      ]
    },
    {
      id: "s8_declaration",
      title: "SECTION 8: DECLARATION & CONSENT",
      description: "I certify that all information provided in this application is true and accurate. I authorize Eliada Sports Agency to verify my experience, references, and professional background where necessary. I consent to the collection, storage, and processing of my personal information for evaluation, recruitment, and professional engagement purposes.",
      fields: [
        { id: "agree_terms", label: "I Agree", type: "checkbox", required: true },
        { id: "signature", label: "Electronic Signature", type: "signature", required: true, description: "Type your full name as your electronic signature" },
        { id: "declaration_date", label: "Date", type: "date", required: true },
      ]
    },
  ],
  submitButtonText: "SUBMIT COACH APPLICATION",
  successMessage: "Thank you for your interest in joining Eliada Sports Agency. Applications are reviewed by our recruitment and athlete development team. Shortlisted candidates may be contacted for interviews, assessments, or discussions regarding coaching opportunities.",
};
